import { usePopper } from 'react-popper'
import {
  useRef, useState, useCallback, useMemo,
} from 'react'
import { useOutsideClick } from './use-outside-click'

/**
 * @callback CbWithoutParams
 * @return {void}
 */

/**
 * @typedef {Object} API
 *
 * @property {Boolean} isPopperOpen
 * @property {CbWithoutParams} closePopper
 * @property {CbWithoutParams} openPopper
 * @property {import('react').MutableRefObject<HTMLElement>} popperRef
 * @property {import('react').SetStateAction<HTMLElement>} setPopperElement
 * @property {Object} popper popper details
 */

/**
 * custom hook. Contain settings and details of popper integration
 * @returns {API}
 */
export function usePopperDialog () {
  const [isPopperOpen, setIsPopperOpen] = useState(false)

  const popperRef = useRef(null)
  const [popperElement, setPopperElement] = useState(null)

  const popper = usePopper(popperRef.current, popperElement, {
    placement: 'bottom-start',
  })

  const closePopper = useCallback(() => {
    setIsPopperOpen(false)
  }, [])

  const openPopper = useCallback(() => {
    setIsPopperOpen(true)
  }, [])

  const componentElements = useMemo(
    () => [popperRef.current, popperElement],
    [popperElement],
  )

  useOutsideClick(componentElements, closePopper)

  const API = useMemo(
    () => ({
      isPopperOpen,
      closePopper,
      openPopper,
      popperRef,
      setPopperElement,
      popper,
    }),
    [closePopper, isPopperOpen, openPopper, popper],
  )
  return API
}
